import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsBelow } from '@/hooks/useBreakpoint';

export interface FilterSelectOption {
  label: string;
  value: string;
  count?: number;
  /** Saturated colour used for the status dot. */
  color?: string;
  /** Readable variant of `color`, used for the label + count text. */
  textColor?: string;
}

export interface FilterSelectProps {
  options: FilterSelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Accessible name for the trigger, e.g. "Filter by status". */
  label?: string;
  className?: string;
}

/**
 * Compact single-select filter — replaces a wide row of filter tabs.
 * The trigger shows the active option (label + count); opening it reveals
 * every option with its own colour-coded dot and count.
 */
export function FilterSelect({ options, value, onChange, label, className }: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  /** Touch tier. Drives outside-dismissal and whether a search field autofocuses. */
  const isTouchTier = useIsBelow('md');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    // Below `md`, `pointerdown`: iOS Safari dispatches no mouse event at all for a tap
    // on a non-interactive element, which leaves this panel undismissable on a phone.
    // Desktop deliberately stays on `mousedown`. Same treatment, and same reasoning, as
    // `Select.tsx` — these are its siblings and carried the identical defect.
    const outsideEvent = isTouchTier ? 'pointerdown' : 'mousedown';
    document.addEventListener(outsideEvent, onOutside);
    return () => document.removeEventListener(outsideEvent, onOutside);
  }, [open, isTouchTier]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  }, []);

  const active = options.find((o) => o.value === value) ?? options[0];

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        className={cn(
          'flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white',
          'px-3 py-1.5 text-sm text-left transition-colors cursor-pointer',
          'hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          value !== '' && 'border-blue-300 bg-blue-50/50',
        )}
      >
        {active?.color && (
          <span
            className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
            style={{ backgroundColor: active.color }}
          />
        )}
        <span
          className="flex-1 truncate font-medium"
          style={{ color: active?.textColor ?? '#0f172a' }}
        >
          {active?.label}
        </span>
        {active?.count != null && (
          <span className="font-semibold text-slate-500">{active.count.toLocaleString()}</span>
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 flex-shrink-0 text-slate-400 transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* Options panel */}
      {open && (
        <div
          role="listbox"
          onKeyDown={handleKeyDown}
          className={cn(
            'absolute z-50 mt-1 w-full min-w-[15rem] origin-top rounded-md',
            'border border-slate-200 bg-white shadow-lg animate-scale-in',
          )}
        >
          <div className="max-h-72 overflow-y-auto py-1">
            {options.map((opt) => {
              const selected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                    'transition-colors hover:bg-slate-50',
                    selected && 'bg-blue-50/60',
                  )}
                >
                  {opt.color ? (
                    <span
                      className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: opt.color }}
                    />
                  ) : (
                    <span className="inline-block h-2.5 w-2.5 flex-shrink-0" />
                  )}
                  <span
                    className={cn('flex-1 truncate', selected && 'font-semibold')}
                    style={{ color: opt.textColor ?? '#334155' }}
                  >
                    {opt.label}
                  </span>
                  {opt.count != null && (
                    <span className="font-semibold text-slate-500 tabular-nums">
                      {opt.count.toLocaleString()}
                    </span>
                  )}
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 flex-shrink-0 text-blue-600',
                      !selected && 'invisible',
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
